import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Input,
  InputGroup,
  Table,
  Button,
  DOMHelper,
  Stack,
  IconButton,
  Whisper,
  Popover,
  Dropdown,
  Modal,
  Loader
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import { Edit, Icon, More } from '@rsuite/icons';
import { MdAdd, MdEdit, MdDelete, MdMoreVert, MdOutlineDeleteOutline } from 'react-icons/md';
import DrawerView from './DrawerView';
import EditDrawerView from './EditDrawerView';
import { getServicelines, removeServiceLine } from './../../../services/serviceline.service';
import { useDispatch, useSelector } from 'react-redux';
import { t } from 'i18next';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const DataTable = () => {
  const dispatch = useDispatch();
  const serviceLines = useSelector(state => state.serviceLine.data);
  const loading = useSelector(state => state.serviceLine.loading);

  useEffect(() => {
    dispatch(getServicelines());
  }, [dispatch]);

  const [showDrawer, setShowDrawer] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleEdit = rowData => {
    setSelectedDepartment(rowData);
    setShowEditDrawer(true);
  };

  const handleDelete = rowData => {
    setSelectedDepartment(rowData);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (selectedDepartment?._id) {
      dispatch(removeServiceLine(selectedDepartment._id));
    }
    setShowDeleteModal(false);
  };

  const renderMenu = ({ onClose, left, top, className }, ref, rowData) => {
    const handleSelect = eventKey => {
      onClose();
      switch (eventKey) {
        case 'edit':
          handleEdit(rowData);
          break;
        case 'delete':
          handleDelete(rowData);
          break;
      }
    };
    const { t } = useTranslation();
    return (
      <Popover ref={ref} className={className} style={{ left, top }} full>
        <Dropdown.Menu onSelect={handleSelect}>
          <Dropdown.Item eventKey="edit">{t('Edit')}</Dropdown.Item>
          <Dropdown.Item eventKey="delete">{t('Delete')}</Dropdown.Item>
        </Dropdown.Menu>
      </Popover>
    );
  };

  const ActionCell = ({ rowData, ...props }) => {
    return (
      <Cell {...props} className="link-group">
        <Whisper
          placement="autoVerticalEnd"
          trigger="click"
          speaker={({ onClose, left, top, className }, ref) =>
            renderMenu({ onClose, left, top, className }, ref, rowData)
          }
        >
          <IconButton appearance="subtle" icon={<Icon as={MdMoreVert} />} size="sm" />
        </Whisper>
      </Cell>
    );
  };

  const filteredDepartments = serviceLines.filter(serviceLine =>
    serviceLine.serviceLine.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <InputGroup inside>
            <Input
              placeholder={t("Search")}
              value={searchKeyword}
              onChange={value => setSearchKeyword(value)}
            />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
        </Stack>

        <Stack spacing={6}>
          <Button appearance="primary" onClick={() => setShowDrawer(true)}>
            <Icon as={MdAdd} style={{ marginRight: 8 }} /> {t('Add Team')}
          </Button>
        </Stack>
      </Stack>
      {loading ? (
        <Loader backdrop />
      ) : (
        <Table height={Math.max(getHeight(window) - 200, 400)} data={filteredDepartments}>
          <Column flexGrow={4}>
            <HeaderCell>{t('Team')}</HeaderCell>
            <Cell dataKey="serviceLine" />
          </Column>

          <Column width={50} flexGrow={0.5} fixed="right">
            <HeaderCell>
              {' '}
              <More />
            </HeaderCell>
            {/* <ActionCell /> */}
            <Cell style={{ padding: '6px' }}>
              {rowData => (
                <Stack>
                  <IconButton
                    onClick={() => {
                      handleEdit(rowData);
                    }}
                    size="xs"
                    icon={<Edit color="blue" />}
                    id={`edit-${rowData._id}`}
                  />
                  <IconButton
                    size="xs"
                    icon={<MdOutlineDeleteOutline color="red" />}
                    onClick={() => {
                      handleDelete(rowData);
                    }}
                    id={`delete-${rowData._id}`}
                  />
                </Stack>
              )}
            </Cell>
          </Column>
        </Table>
      )}

      <DrawerView open={showDrawer} onClose={() => setShowDrawer(false)} />

      <EditDrawerView
        open={showEditDrawer}
        department={selectedDepartment}
        onClose={() => setShowEditDrawer(false)}
      />

      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>{t('Delete Team')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('Are you sure you want to delete this team? This action cannot be undone.')}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowDeleteModal(false)} appearance="subtle">
            {t('Cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} appearance="primary" color="red">
            {t('Delete')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DataTable;
